import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { ADMIN_SEARCH_INDEX } from "../../config/adminSearchIndex";
import {
  Search,
  MessageSquareText,
  ShoppingCart,
  CreditCard,
  LifeBuoy,
  Wifi,
  WifiOff,
  Volume2,
  VolumeX,
  Bell,
  BellOff,
  Command,
  ArrowRight,
} from "lucide-react";

/* Design tokens */
const BRAND = "#01C37D";
const BORDER = "#EEF2F6";
const TEXT = "#101828";
const HOVER_BG = "rgba(1,195,125,0.10)";
const MUTED = "#667085";

/* Role color map */
function roleColors(userType = "") {
  const r = String(userType).toLowerCase();
  if (r.includes("super"))
    return { bg: "#FEE2E2", text: "#B91C1C", ring: "#FCA5A5" };
  if (r.includes("manager"))
    return { bg: "#DBEAFE", text: "#1E40AF", ring: "#93C5FD" };
  if (r.includes("admin"))
    return { bg: "#DCFCE7", text: "#065F46", ring: "#86EFAC" };
  return { bg: "#F3F4F6", text: "#374151", ring: "#E5E7EB" };
}

function IconButton({ title, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="h-9 w-9 flex items-center justify-center rounded-lg border transition-colors"
      style={{ borderColor: BORDER, backgroundColor: "white" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER_BG)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      {children}
    </button>
  );
}

function useNetworkStatus({ intervalMs = 20000 } = {}) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [speedMbps, setSpeedMbps] = useState(null);
  const [label, setLabel] = useState("");
  const timerRef = React.useRef(null);

  const readNavigatorConnection = () => {
    const c =
      navigator.connection ||
      navigator.webkitConnection ||
      navigator.mozConnection;
    if (!c) return null;
    const downlink = typeof c.downlink === "number" ? c.downlink : null;
    const eff = c.effectiveType || "";
    const rtt = typeof c.rtt === "number" ? c.rtt : null;
    return { downlink, eff, rtt };
  };

  const measureFetch = async () => {
    const url = `/logo192.png?cb=${Date.now()}`;
    const started = performance.now();
    try {
      const res = await fetch(url, { cache: "no-store" });
      const buf = await res.arrayBuffer();
      const ended = performance.now();
      const bytes = buf.byteLength || 0;
      const seconds = Math.max((ended - started) / 1000, 0.001);
      const mbps = (bytes * 8) / seconds / (1024 * 1024);
      return { mbps };
    } catch {
      return null;
    }
  };

  const update = async () => {
    const online = navigator.onLine;
    setIsOnline(online);

    if (!online) {
      setSpeedMbps(null);
      setLabel("offline");
      return;
    }

    const conn = readNavigatorConnection();
    if (conn?.downlink) {
      setSpeedMbps(conn.downlink);
      setLabel(
        conn.eff ? `${conn.eff}${conn.rtt ? ` · ${conn.rtt}ms` : ""}` : "online"
      );
      return;
    }

    const result = await measureFetch();
    if (result?.mbps != null) {
      setSpeedMbps(result.mbps);
      setLabel("online");
    } else {
      setSpeedMbps(null);
      setLabel("online");
    }
  };

  useEffect(() => {
    const onOn = () => update();
    const onOff = () => update();

    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    update();

    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!isOnline) return;

    timerRef.current = setInterval(() => {
      if (document.visibilityState === "visible") update();
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOnline, intervalMs]);

  return { isOnline, speedMbps, label };
}

function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getSearchScore(item, rawQuery) {
  const query = normalizeText(rawQuery);
  if (!query) return 0;

  const label = normalizeText(item.label);
  const description = normalizeText(item.description);
  const path = normalizeText(item.path);
  const section = normalizeText(item.section);
  const keywords = (item.keywords || []).map(normalizeText);

  let score = 0;

  if (label === query) score += 120;
  if (section === query) score += 100;
  if (keywords.includes(query)) score += 90;

  if (label.startsWith(query)) score += 65;
  if (section.startsWith(query)) score += 50;

  if (label.includes(query)) score += 45;
  if (description.includes(query)) score += 25;
  if (path.includes(query)) score += 20;
  if (section.includes(query)) score += 35;

  for (const keyword of keywords) {
    if (keyword === query) score += 90;
    else if (keyword.startsWith(query)) score += 45;
    else if (keyword.includes(query)) score += 25;
  }

  const queryWords = query.split(" ").filter(Boolean);
  const haystack = [label, description, path, section, ...keywords].join(" ");

  const allWordsMatch = queryWords.every((word) => haystack.includes(word));
  if (allWordsMatch) score += 30;

  return score;
}

function buildSearchResults(query) {
  const scored = ADMIN_SEARCH_INDEX.map((item) => ({
    ...item,
    score: getSearchScore(item, query),
  }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .slice(0, 8);

  return scored;
}

function SearchResultItem({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors"
      style={{
        backgroundColor: active ? "rgba(1,195,125,0.10)" : "white",
      }}
    >
      <div
        className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center"
        style={{ backgroundColor: "rgba(1,195,125,0.12)" }}
      >
        <Command size={16} style={{ color: BRAND }} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold truncate" style={{ color: TEXT }}>
            {item.label}
          </div>
          <div
            className="text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap"
            style={{
              borderColor: "#D0D5DD",
              color: MUTED,
              backgroundColor: "#F9FAFB",
            }}
          >
            {item.path}
          </div>
        </div>

        <div className="text-xs mt-0.5 truncate" style={{ color: MUTED }}>
          {item.description}
        </div>

        {item.section ? (
          <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "#475467" }}>
            <ArrowRight size={12} />
            <span>Section: {item.section}</span>
          </div>
        ) : null}
      </div>
    </button>
  );
}

export default function Topbar() {
  const {
    currentAdmin,
    ordersSoundMuted,
    setOrdersSoundMuted,
    ordersNotificationsEnabled,
    setOrdersNotificationsEnabled,
  } = useContext(GlobalContext);

  const navigate = useNavigate();

  const { name, userType } = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      const ss = raw ? JSON.parse(raw) : null;
      return {
        name: currentAdmin?.name || ss?.adminMeta?.adminName || "Admin",
        userType:
          currentAdmin?.userType ||
          ss?.userType ||
          ss?.adminMeta?.userType ||
          "Admin",
      };
    } catch {
      return { name: currentAdmin?.name || "Admin", userType: "Admin" };
    }
  }, [currentAdmin]);

  const rc = roleColors(userType);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const weekdayStr = now.toLocaleDateString(undefined, { weekday: "short" });
  const dateStr = now.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const initials = useMemo(() => {
    const parts = String(name).trim().split(/\s+/);
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  }, [name]);

  const quick = [
    { title: "WhatsApp", to: "/admin/whatsapp", Icon: MessageSquareText },
    { title: "Orders", to: "/admin/orders", Icon: ShoppingCart },
    { title: "Payments", to: "/admin/payout", Icon: CreditCard },
    { title: "Support", to: "/admin/support", Icon: LifeBuoy },
  ];

  const { isOnline, speedMbps, label } = useNetworkStatus({
    intervalMs: 20000,
  });

  const statusColors = isOnline
    ? {
        bg: "rgba(1,195,125,0.10)",
        border: BRAND,
        textLeft: "#065F46",
        textRight: "#0B3B2C",
      }
    : {
        bg: "#FEE2E2",
        border: "#EF4444",
        textLeft: "#991B1B",
        textRight: "#7F1D1D",
      };

  const handleToggleSound = () => {
    setOrdersSoundMuted((prev) => !prev);
  };

  const handleToggleNotification = () => {
    setOrdersNotificationsEnabled((prev) => !prev);
  };

  const [search, setSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const searchWrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const results = useMemo(() => buildSearchResults(search), [search]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!searchWrapperRef.current?.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openSearchResult = (item) => {
    if (!item) return;

    const hasSection = item.section && String(item.section).trim();
    const url = hasSection
      ? `${item.path}?focus=${encodeURIComponent(item.section)}`
      : item.path;

    navigate(url);
    setSearch("");
    setIsSearchOpen(false);
    setActiveIndex(0);
    searchInputRef.current?.blur();
  };

  const handleInputKeyDown = (e) => {
    if (!results.length) {
      if (e.key === "Enter" && search.trim()) {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsSearchOpen(true);
      setActiveIndex((prev) => (prev + 1) % results.length);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setIsSearchOpen(true);
      setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const selected = results[activeIndex] || results[0];
      openSearchResult(selected);
    }

    if (e.key === "Escape") {
      setIsSearchOpen(false);
      searchInputRef.current?.blur();
    }
  };

  return (
    <header
      className="h-16 bg-white border-b flex items-center justify-between px-3 lg:px-5"
      style={{ borderColor: BORDER }}
    >
      {/* Left: Search */}
      <div ref={searchWrapperRef} className="relative w-full max-w-xl">
        <div
          className="flex items-center gap-2 w-full rounded-lg border bg-white px-3 py-2"
          style={{ borderColor: BORDER }}
        >
          <Search size={18} className="text-gray-500 shrink-0" />

          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsSearchOpen(true);
            }}
            onFocus={() => setIsSearchOpen(true)}
            onKeyDown={handleInputKeyDown}
            placeholder="Search pages, actions, settings..."
            className="w-full outline-none text-sm"
            aria-label="Search admin features"
          />

          {search ? (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setIsSearchOpen(false);
                setActiveIndex(0);
                searchInputRef.current?.focus();
              }}
              className="text-xs font-medium px-2 py-1 rounded-md border"
              style={{ borderColor: BORDER, color: MUTED }}
            >
              Clear
            </button>
          ) : null}
        </div>

        {isSearchOpen && search.trim() ? (
          <div
            className="absolute top-[calc(100%+8px)] left-0 right-0 rounded-xl border bg-white shadow-xl overflow-hidden z-50"
            style={{ borderColor: BORDER }}
          >
            {results.length ? (
              <>
                <div
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide border-b"
                  style={{ color: MUTED, borderColor: BORDER, backgroundColor: "#F9FAFB" }}
                >
                  Search Results
                </div>

                <div className="max-h-[380px] overflow-y-auto">
                  {results.map((item, index) => (
                    <SearchResultItem
                      key={item.id}
                      item={item}
                      active={index === activeIndex}
                      onClick={() => openSearchResult(item)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="px-4 py-4 text-sm" style={{ color: MUTED }}>
                No matching result found for <strong>{search}</strong>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="hidden md:flex items-center px-4" />

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Status Card */}
        <div
          className="flex items-stretch rounded-xl border overflow-hidden"
          style={{
            backgroundColor: statusColors.bg,
            borderColor: statusColors.border,
          }}
          title={isOnline ? label || "online" : "offline"}
        >
          <div className="flex items-center justify-center px-3 py-2 min-w-[110px]">
            <div
              className="text-xs me-2 font-semibold tracking-wide"
              style={{ color: statusColors.textLeft }}
            >
              {weekdayStr}
            </div>
            <div className="text-sm font-medium" style={{ color: TEXT }}>
              {dateStr}
            </div>
          </div>

          <div
            className="w-px my-2"
            style={{ backgroundColor: statusColors.border, opacity: 0.6 }}
          />

          <div className="flex items-center gap-3 pl-3 pr-3">
            <div
              className="text-sm font-semibold tabular-nums"
              style={{ color: statusColors.textRight }}
            >
              {timeStr}
            </div>
            <div className="flex items-center gap-1 text-xs font-medium">
              {isOnline ? (
                <>
                  <Wifi size={16} style={{ color: BRAND }} />
                  <span style={{ color: statusColors.textRight }}>
                    Online
                    {typeof speedMbps === "number"
                      ? ` · ${speedMbps.toFixed(1)} Mbps`
                      : ""}
                  </span>
                </>
              ) : (
                <>
                  <WifiOff size={16} style={{ color: "#DC2626" }} />
                  <span style={{ color: statusColors.textRight }}>Offline</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick action buttons */}
        <div className="flex items-center gap-2">
          {quick.map(({ title, to, Icon }) => (
            <IconButton key={title} title={title} onClick={() => navigate(to)}>
              <Icon size={18} style={{ color: BRAND }} />
            </IconButton>
          ))}

          <IconButton
            title={ordersSoundMuted ? "Unmute order sound" : "Mute order sound"}
            onClick={handleToggleSound}
          >
            {ordersSoundMuted ? (
              <VolumeX size={18} style={{ color: "#EF4444" }} />
            ) : (
              <Volume2 size={18} style={{ color: BRAND }} />
            )}
          </IconButton>

          <IconButton
            title={
              ordersNotificationsEnabled
                ? "Turn off notifications"
                : "Turn on notifications"
            }
            onClick={handleToggleNotification}
          >
            {ordersNotificationsEnabled ? (
              <Bell size={18} style={{ color: BRAND }} />
            ) : (
              <BellOff size={18} style={{ color: "#9CA3AF" }} />
            )}
          </IconButton>
        </div>

        {/* User block */}
        <div className="flex items-center gap-3">
          <div className="text-right leading-tight">
            <div
              className="text-sm font-semibold truncate"
              style={{ color: TEXT }}
            >
              {name}
            </div>
            <div className="text-xs truncate" style={{ color: rc.text }}>
              {userType}
            </div>
          </div>
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold"
            style={{
              backgroundColor: "#FFFFFF",
              color: rc.text,
              border: `2px solid ${rc.ring}`,
              boxShadow: "0 1px 0 rgba(16,24,40,0.04)",
            }}
            title={userType}
          >
            {initials || "AD"}
          </div>
        </div>
      </div>
    </header>
  );
}